/**
 * Wave 3 · 23 #8 — Forecast from real demand (pure helpers for GET /forecast/demand).
 * The server works out, per part and week, need (open site requests + BOQ still to supply on Active projects at
 * their planned dates) vs have (stock − booked + LPO quantities expected that week; late ones are not counted)
 * and the week the part runs out. These helpers only shape it for the page.
 */
import { formatDate, formatNumber, toNumber } from '@/lib/format';

const num = (value) => toNumber(value) ?? 0;
const text = (value) => String(value ?? '').trim();

export const weekLabel = (start) => formatDate(start, { year: 'never' });

/** A week cell's tone: red = runs short that week, amber = need eats the last pieces, '' = covered / nothing needed. */
export function weekTone(balance, need) {
  if (num(balance) < 0) return 'red';
  if (num(need) > 0 && num(balance) === 0) return 'amber';
  return '';
}

/** "Week of 05 Oct · need 6 · 30 arriving · 4 left" (the cell's tooltip / accessible name). */
export function weekTitle(part, index, weeks) {
  const week = weeks[index];
  const need = num(part.need?.[index]);
  const incoming = num(part.incoming?.[index]);
  const balance = num(part.balance?.[index]);
  const bits = [`Week of ${weekLabel(week?.start)}`];
  bits.push(need ? `need ${need}` : 'nothing needed');
  if (incoming) bits.push(`${incoming} arriving`);
  bits.push(balance < 0 ? `${-balance} short` : `${balance} left`);
  return bits.join(' · ');
}

const qtyText = (value) => formatNumber(value, { maxDecimals: 2 });
const dayText = (value) => (text(value) ? formatDate(value, { year: 'auto' }) : '');

/** Procurement › Orders with that draft highlighted (22 #9). */
export const draftHref = (number) => `/procurement?tab=orders&lpo=${encodeURIComponent(text(number))}`;

/**
 * What to do about a part that runs out (the server's `run_out.action`):
 *  - expedite   short for a while, but an LPO expected later in the window covers it → chase that LPO, order nothing;
 *  - send_draft still short after every incoming LPO, but draft LPOs not sent yet cover it → send the draft;
 *  - order      short after the incoming LPOs and the drafts → order the rest (by the order-by date).
 * A run-out without `action` (older server) is an order.
 * → { action, tone: 'red' | 'amber' | 'navy' | '', step, sub, sentence, draft, cover, toOrder }
 */
export function runAdvice(run, unit = 'pcs') {
  if (!run) return null;
  const action = ['expedite', 'send_draft', 'order'].includes(run.action) ? run.action : 'order';
  const drafts = Array.isArray(run.drafts) ? run.drafts : [];
  const cover = Array.isArray(run.cover) ? run.cover : [];
  const late = Boolean(run.order_late);
  const orderBy = dayText(run.order_by);
  if (action === 'expedite') {
    const first = cover[0];
    const arrives = first ? `${first.lpo_number} arrives ${dayText(first.expected)}` : 'An LPO arrives later';
    return {
      action, tone: 'amber', step: 'Expedite', cover: first || null, draft: null, toOrder: 0,
      sub: first ? `${first.lpo_number} · ${dayText(first.expected)}` : 'LPO arrives later',
      sentence: `${arrives} — expedite it; nothing to order.`,
    };
  }
  if (action === 'send_draft') {
    const draft = drafts[0] || { lpo_number: '', status: '' };
    const which = `${draft.lpo_number}${draft.status ? ` (${draft.status})` : ''}`;
    return {
      action, tone: late ? 'red' : 'navy', step: late ? 'Send draft now' : `Send draft by ${orderBy}`, draft, cover: cover[0] || null, toOrder: 0,
      sub: `${draft.lpo_number}${draft.status ? ` · ${draft.status}` : ''}${drafts.length > 1 ? ` +${drafts.length - 1}` : ''}`,
      sentence: `It is on draft ${which} — send it ${late ? 'now: the lead time has already passed' : `by ${orderBy}`}.`,
    };
  }
  const toOrder = num(run.to_order_qty);
  const onDraft = drafts.length && num(run.draft_qty) > 0 ? `${qtyText(run.draft_qty)} are on draft ${drafts[0].lpo_number}` : '';
  const head = onDraft && toOrder > 0 ? `Order ${qtyText(toOrder)} ${unit} more` : 'Order';
  return {
    action, tone: late ? 'red' : '', step: late ? 'Order now' : `Order by ${orderBy}`, draft: drafts[0] || null, cover: cover[0] || null, toOrder,
    sub: onDraft && toOrder > 0 ? `${qtyText(toOrder)} more · ${drafts[0].lpo_number}` : '',
    sentence: onDraft
      ? `${head} ${late ? 'now — the lead time has already passed' : `by ${orderBy}`} (${onDraft}).`
      : late ? 'Order now — the lead time has already passed.' : `Order by ${orderBy}.`,
  };
}

/** The headline under the card head: the first part that runs out, or that nothing does. */
export function outlookHeadline(outlook) {
  const first = outlook?.summary?.first_run_out;
  if (!first) return null;
  const part = (outlook.parts || []).find((item) => item.part_number === first.part_number);
  const run = part?.run_out;
  const unit = text(part?.unit) || 'pcs';
  const advice = runAdvice(run, unit);
  return {
    part: first.part_number,
    description: text(part?.description),
    week: weekLabel(first.week_start),
    project: text(first.project_name),
    short: num(first.short_qty),
    unit,
    orderBy: run && advice?.action !== 'expedite' ? dayText(run.order_by) : '',
    orderLate: advice?.action === 'expedite' ? false : Boolean(run?.order_late),
    advice,
  };
}

/** "2 to order · 2 on drafts · 1 to expedite" (what the run-out parts need), '' when nothing runs out. */
export function adviceCounts(summary) {
  if (!summary || !('to_order' in summary)) return '';
  return [
    num(summary.to_order) ? `${qtyText(summary.to_order)} to order` : '',
    num(summary.on_drafts) ? `${qtyText(summary.on_drafts)} on drafts` : '',
    num(summary.to_expedite) ? `${qtyText(summary.to_expedite)} to expedite` : '',
  ].filter(Boolean).join(' · ');
}

export const DEMAND_VIEWS = [
  { key: 'short', label: 'Runs out' },
  { key: 'all', label: 'All needed' },
];

export function filterDemandParts(parts, { view = 'short', search = '' } = {}) {
  const needle = text(search).toLowerCase();
  return (Array.isArray(parts) ? parts : []).filter((part) => {
    if (view === 'short' && !part.run_out) return false;
    if (!needle) return true;
    return [part.part_number, part.description, ...(part.projects || []).map((row) => row.project_name)]
      .some((value) => text(value).toLowerCase().includes(needle));
  });
}
